import React from 'react'
import {
  BlueGreenConfigurationAuditSnapshotV1,
  CertificateConfigurationAuditSnapshotV1,
  CustomerInterfaceImportConfigurationAuditSnapshotV1,
  CustomerInterfaceLoggingConfigBulkEditAuditSnapshotV1,
  DatabaseShardConfigurationAuditSnapshotV1,
  DiagnosticConfigurationAuditSnapshotV1,
  IAuditEventData,
  LocationSharingConfigurationAuditSnapshotV1,
  NpgsqlConfigurationAuditSnapshotV1,
  SupportFedAuthConfigurationAuditSnapshotV1,
} from 'api'
import { Box } from 'cs-kevlar-react'
import { DetailField } from './DetailField'
import { useDetailStyles } from './useDetailStyles'
import { resolveEventData, resolveSnapshots, zipSnapshots } from './utils'

const hasSnapshots = (snapshots: (any | null)[]): boolean => {
  return !!snapshots[0] || !!snapshots[1]
}

interface DiagnosticDetailsProps {
  eventData: IAuditEventData
}

export const DiagnosticDetails: React.FunctionComponent<DiagnosticDetailsProps> = ({
  eventData,
}) => {
  const classes = useDetailStyles()

  const diagnosticConfigSnapshots = resolveSnapshots<
    DiagnosticConfigurationAuditSnapshotV1
  >(eventData, nameof<DiagnosticConfigurationAuditSnapshotV1>())
  const npgSqlConfigSnapshots = resolveSnapshots<
    NpgsqlConfigurationAuditSnapshotV1
  >(eventData, nameof<NpgsqlConfigurationAuditSnapshotV1>())
  const deviceLocationSharingConfigSnapshots = resolveSnapshots<
    LocationSharingConfigurationAuditSnapshotV1
  >(eventData, nameof<LocationSharingConfigurationAuditSnapshotV1>())
  const connectorImportConfigSnapshots = resolveSnapshots<
    CustomerInterfaceImportConfigurationAuditSnapshotV1
  >(eventData, nameof<CustomerInterfaceImportConfigurationAuditSnapshotV1>())
  const certificateConfigSnapshots = resolveSnapshots<
    CertificateConfigurationAuditSnapshotV1
  >(eventData, nameof<CertificateConfigurationAuditSnapshotV1>())
  const fedAuthConfigSnapshots = resolveSnapshots<
    SupportFedAuthConfigurationAuditSnapshotV1
  >(eventData, nameof<SupportFedAuthConfigurationAuditSnapshotV1>())
  const connectorLoggingConfigSnapshot = resolveEventData<
    CustomerInterfaceLoggingConfigBulkEditAuditSnapshotV1
  >(eventData, nameof<CustomerInterfaceLoggingConfigBulkEditAuditSnapshotV1>())
  const blueGreenConfigSnapshots = resolveSnapshots<
    BlueGreenConfigurationAuditSnapshotV1
  >(eventData, nameof<BlueGreenConfigurationAuditSnapshotV1>())
  const databaseShardConfigSnapshots = resolveSnapshots<
    DatabaseShardConfigurationAuditSnapshotV1
  >(eventData, nameof<DatabaseShardConfigurationAuditSnapshotV1>())

  return (
    <React.Fragment>
      {hasSnapshots(diagnosticConfigSnapshots) && (
        <Box className={classes.fieldsContainer}>
          <DetailField
            label="Name"
            showDiff
            original={
              diagnosticConfigSnapshots[0]?.runtimeGlobalLogLevelOverride
            }
            updated={
              diagnosticConfigSnapshots[1]?.runtimeGlobalLogLevelOverride
            }
          />

          {zipSnapshots(
            diagnosticConfigSnapshots[0]
              ?.runtimeSystemComponentLogLevelOverrides,
            diagnosticConfigSnapshots[1]
              ?.runtimeSystemComponentLogLevelOverrides,
          ).map((x) => (
            <DetailField
              key={x.key}
              label={x.key}
              showDiff
              original={x.original}
              updated={x.updated}
            />
          ))}
        </Box>
      )}

      {hasSnapshots(npgSqlConfigSnapshots) && (
        <Box className={classes.fieldsContainer}>
          <DetailField
            label="Logging Enabled"
            showDiff
            original={npgSqlConfigSnapshots[0]?.eventListenerEnabled?.toString()}
            updated={npgSqlConfigSnapshots[1]?.eventListenerEnabled?.toString()}
          />

          <DetailField
            label="Event Listener Interval in Seconds"
            showDiff
            original={npgSqlConfigSnapshots[0]?.eventListenerIntervalInSeconds?.toString()}
            updated={npgSqlConfigSnapshots[1]?.eventListenerIntervalInSeconds?.toString()}
          />

          <DetailField
            label="SQL Diagnostic Logging Enabled"
            showDiff
            original={npgSqlConfigSnapshots[0]?.sqlDiagnosticLoggingEnabled?.toString()}
            updated={npgSqlConfigSnapshots[1]?.sqlDiagnosticLoggingEnabled?.toString()}
          />

          <DetailField
            label="SQL Parameter Logging Enabled"
            showDiff
            original={npgSqlConfigSnapshots[0]?.sqlParameterLoggingEnabled?.toString()}
            updated={npgSqlConfigSnapshots[1]?.sqlParameterLoggingEnabled?.toString()}
          />
        </Box>
      )}

      {hasSnapshots(deviceLocationSharingConfigSnapshots) && (
        <Box className={classes.fieldsContainer}>
          <DetailField
            label="Force Override All Device Location Sharing Configurations"
            showDiff
            original={deviceLocationSharingConfigSnapshots[0]?.forceOverrideAllDeviceConfigurations?.toString()}
            updated={deviceLocationSharingConfigSnapshots[1]?.forceOverrideAllDeviceConfigurations?.toString()}
          />

          <DetailField
            label="Update Interval in Seconds"
            showDiff
            original={deviceLocationSharingConfigSnapshots[0]?.deviceLocationSharingIntervalInSeconds?.toString()}
            updated={deviceLocationSharingConfigSnapshots[1]?.deviceLocationSharingIntervalInSeconds?.toString()}
          />
        </Box>
      )}

      {hasSnapshots(connectorImportConfigSnapshots) && (
        <Box className={classes.fieldsContainer}>
          <DetailField
            label="Enable Legacy Document Import Deduping"
            showDiff
            original={connectorImportConfigSnapshots[0]?.enableLegacyDocumentImportDeduping?.toString()}
            updated={connectorImportConfigSnapshots[1]?.enableLegacyDocumentImportDeduping?.toString()}
          />

          <DetailField
            label="Enable Document Import Deduping"
            showDiff
            original={connectorImportConfigSnapshots[0]?.enableDocumentImportDeduping?.toString()}
            updated={connectorImportConfigSnapshots[1]?.enableDocumentImportDeduping?.toString()}
          />

          <DetailField
            label="Enable Lambda Thumbnail Generation"
            showDiff
            original={connectorImportConfigSnapshots[0]?.enableLambdaThumbnailGeneration?.toString()}
            updated={connectorImportConfigSnapshots[1]?.enableLambdaThumbnailGeneration?.toString()}
          />

          <DetailField
            label="Enable S3 File Document Cleanup"
            showDiff
            original={connectorImportConfigSnapshots[0]?.enableS3FileDocumentCleanup?.toString()}
            updated={connectorImportConfigSnapshots[1]?.enableS3FileDocumentCleanup?.toString()}
          />

          <DetailField
            label="Import Lock Acquisition Delay In Seconds"
            showDiff
            original={connectorImportConfigSnapshots[0]?.importLockAcquisitionDelayInSeconds?.toString()}
            updated={connectorImportConfigSnapshots[1]?.importLockAcquisitionDelayInSeconds?.toString()}
          />

          <DetailField
            label="Enable Statusing Document Debug Logs"
            showDiff
            original={connectorImportConfigSnapshots[0]?.enableStatusingDocumentDebugLogs?.toString()}
            updated={connectorImportConfigSnapshots[1]?.enableStatusingDocumentDebugLogs?.toString()}
          />

          <DetailField
            label="Enable Device Avl Push Monitoring"
            showDiff
            original={connectorImportConfigSnapshots[0]?.enableDeviceAvlPushMonitoring?.toString()}
            updated={connectorImportConfigSnapshots[1]?.enableDeviceAvlPushMonitoring?.toString()}
          />
        </Box>
      )}

      {hasSnapshots(certificateConfigSnapshots) && (
        <Box className={classes.fieldsContainer}>
          <DetailField
            label="Force Certificate Generation Error"
            showDiff
            original={certificateConfigSnapshots[0]?.forceCertificateGenerationError?.toString()}
            updated={certificateConfigSnapshots[1]?.forceCertificateGenerationError?.toString()}
          />

          <DetailField
            label="Device Certificate Duration Seconds Override"
            showDiff
            original={certificateConfigSnapshots[0]?.deviceCertificateDurationSecondsOverride?.toString()}
            updated={certificateConfigSnapshots[1]?.deviceCertificateDurationSecondsOverride?.toString()}
          />

          <DetailField
            label="Connector Certificate Duration Seconds Override"
            showDiff
            original={certificateConfigSnapshots[0]?.connectorCertificateDurationSecondsOverride?.toString()}
            updated={certificateConfigSnapshots[1]?.connectorCertificateDurationSecondsOverride?.toString()}
          />

          <DetailField
            label="Identity Certificate Duration Seconds Override"
            showDiff
            original={certificateConfigSnapshots[0]?.identityCertificateDurationSecondsOverride?.toString()}
            updated={certificateConfigSnapshots[1]?.identityCertificateDurationSecondsOverride?.toString()}
          />
        </Box>
      )}

      {hasSnapshots(fedAuthConfigSnapshots) && (
        <Box className={classes.fieldsContainer}>
          <DetailField
            label="Client ID"
            showDiff
            original={fedAuthConfigSnapshots[0]?.clientId?.toString()}
            updated={fedAuthConfigSnapshots[1]?.clientId?.toString()}
          />

          <DetailField
            label="Authorize Endpoint"
            showDiff
            original={fedAuthConfigSnapshots[0]?.authorizeEndpoint?.toString()}
            updated={fedAuthConfigSnapshots[1]?.authorizeEndpoint?.toString()}
          />

          <DetailField
            label="Token Endpoint"
            showDiff
            original={fedAuthConfigSnapshots[0]?.tokenEndpoint?.toString()}
            updated={fedAuthConfigSnapshots[1]?.tokenEndpoint?.toString()}
          />

          <DetailField
            label="User Info Endpoint"
            showDiff
            original={fedAuthConfigSnapshots[0]?.userInfoEndpoint?.toString()}
            updated={fedAuthConfigSnapshots[1]?.userInfoEndpoint?.toString()}
          />

          <DetailField
            label="Logout Endpoint"
            showDiff
            original={fedAuthConfigSnapshots[0]?.logoutEndpoint?.toString()}
            updated={fedAuthConfigSnapshots[1]?.logoutEndpoint?.toString()}
          />

          <DetailField
            label="Scope"
            showDiff
            original={fedAuthConfigSnapshots[0]?.scope?.toString()}
            updated={fedAuthConfigSnapshots[1]?.scope?.toString()}
          />

          <DetailField
            label="Grant Type"
            showDiff
            original={fedAuthConfigSnapshots[0]?.grantType?.toString()}
            updated={fedAuthConfigSnapshots[1]?.grantType?.toString()}
          />

          <DetailField
            label="Supports PKCE"
            showDiff
            original={fedAuthConfigSnapshots[0]?.supportsPkce?.toString()}
            updated={fedAuthConfigSnapshots[1]?.supportsPkce?.toString()}
          />

          <DetailField
            label="Tokens"
            showDiff
            original={fedAuthConfigSnapshots[0]?.tokenKeys?.toString()}
            updated={fedAuthConfigSnapshots[1]?.tokenKeys?.toString()}
          />
        </Box>
      )}

      {!!connectorLoggingConfigSnapshot && (
        <Box className={classes.fieldsContainer}>
          <DetailField
            label="Product Line"
            original={connectorLoggingConfigSnapshot.productLine?.toString()}
          />

          <DetailField
            label="Enable Logging"
            original={connectorLoggingConfigSnapshot.configuration?.loggingEnabled?.toString()}
          />

          <DetailField
            label="Log Level"
            original={connectorLoggingConfigSnapshot.configuration?.logLevel?.toString()}
          />

          <DetailField
            label="AWS Enabled"
            original={connectorLoggingConfigSnapshot.configuration?.awsEnabled?.toString()}
          />
        </Box>
      )}

      {hasSnapshots(blueGreenConfigSnapshots) && (
        <Box className={classes.fieldsContainer}>
          <DetailField
            label="Is Blue/Green Running"
            showDiff
            original={blueGreenConfigSnapshots[0]?.isBlueGreenRunning?.toString()}
            updated={blueGreenConfigSnapshots[1]?.isBlueGreenRunning?.toString()}
          />

          <DetailField
            label="Is Import Lock Draining Active"
            showDiff
            original={blueGreenConfigSnapshots[0]?.isImportLockDrainingActive?.toString()}
            updated={blueGreenConfigSnapshots[1]?.isImportLockDrainingActive?.toString()}
          />
        </Box>
      )}

      {hasSnapshots(databaseShardConfigSnapshots) && (
        <Box className={classes.fieldsContainer}>
          <DetailField
            label="Default Shard ID"
            showDiff
            original={databaseShardConfigSnapshots[0]?.defaultShardId.toString()}
            updated={databaseShardConfigSnapshots[1]?.defaultShardId?.toString()}
          />
        </Box>
      )}
    </React.Fragment>
  )
}
